import { useEffect, useState } from "react";

// The badge-image services (github-readme-stats, profile-trophy, …) rate limit
// aggressively and then render a broken image. We read the public GitHub REST
// API directly instead and draw the cards ourselves: no third-party host, no
// baked-in colours that fight the theme, and a real error state we control.
//
// Unauthenticated requests are limited to 60/hour per IP, which is ample for a
// portfolio, and api.github.com sends Access-Control-Allow-Origin: *.
export default function useGithubStats(user) {
  const [state, setState] = useState({
    status: "loading",
    profile: null,
    repos: [],
    languages: [],
    totals: { stars: 0, forks: 0 },
  });

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();

    (async () => {
      try {
        const [profileRes, reposRes] = await Promise.all([
          fetch(`https://api.github.com/users/${user}`, { signal: ac.signal }),
          fetch(
            `https://api.github.com/users/${user}/repos?per_page=100&sort=pushed`,
            { signal: ac.signal }
          ),
        ]);

        if (!profileRes.ok) throw new Error(`profile ${profileRes.status}`);
        if (!reposRes.ok) throw new Error(`repos ${reposRes.status}`);

        const profile = await profileRes.json();
        const repos = await reposRes.json();
        if (cancelled) return;

        const totals = repos.reduce(
          (acc, r) => ({
            stars: acc.stars + (r.stargazers_count || 0),
            forks: acc.forks + (r.forks_count || 0),
          }),
          { stars: 0, forks: 0 }
        );

        // rank languages by how many repos use them as the primary language
        const counts = {};
        repos.forEach((r) => {
          if (!r.language || r.fork) return;
          counts[r.language] = (counts[r.language] || 0) + 1;
        });
        const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
        const languages = Object.entries(counts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6)
          .map(([name, n]) => ({
            name,
            count: n,
            percent: Math.round((n / total) * 100),
          }));

        const top = repos
          .filter((r) => !r.fork)
          .sort(
            (a, b) =>
              b.stargazers_count - a.stargazers_count ||
              new Date(b.pushed_at) - new Date(a.pushed_at)
          )
          .slice(0, 4);

        setState({ status: "ok", profile, repos: top, languages, totals });
      } catch (err) {
        if (cancelled || err.name === "AbortError") return;
        setState((s) => ({ ...s, status: "error" }));
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [user]);

  return state;
}
